import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ModuleCard from '@/components/videos/ModuleCard';
import CourseBreadcrumb from '@/components/videos/CourseBreadcrumb';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import { APIRoutes } from '@/services/api.routes';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import type { Course, Module, CourseID, Video } from '@/models';

const PAGE_SIZE = 50;

const CourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyUnwatched, setShowOnlyUnwatched] = useState(false);
  const [moduleProgress, setModuleProgress] = useState<Record<string, { watched: number; total: number; percent: number }>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalModules, setTotalModules] = useState(0);
  const [loading, setLoading] = useState(true);
  const { hasCourseAccess, hasActiveSubscription } = useFeatureAccess();

  useEffect(() => {
    if (courseId) {
      fetchCourseData();
    }
  }, [courseId, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showOnlyUnwatched]);

  const fetchCourseData = async () => {
    if (!courseId) return;

    try {
      setLoading(true);
      const [courseResponse, modulesResponse, , courseProgressResponse] = await Promise.all([
        APIRoutes.getCourse(courseId as CourseID),
        APIRoutes.listModules(courseId as CourseID, {
          limit: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined
        }),
        // Mark course as started
        APIRoutes.startCourse(courseId as CourseID).catch(err => {
          console.error('Failed to mark course as started:', err);
          return null;
        }),
        // Get course progress
        APIRoutes.getCourseProgress(courseId as CourseID).catch(err => {
          console.error('Failed to get course progress:', err);
          return null;
        })
      ]);

      setCourse(courseResponse);
      setModules(modulesResponse.modules);
      setTotalModules(modulesResponse.total);

      // Fetch all videos for each module to calculate progress
      const videosByModule: Record<string, Video[]> = {};
      const videoPromises = modulesResponse.modules.map(async (module) => {
        try {
          const videosResponse = await APIRoutes.listVideos(module.id, { published: true });
          videosByModule[module.id] = videosResponse.videos;
          return videosResponse.videos;
        } catch (err) {
          console.error(`Failed to fetch videos for module ${module.id}:`, err);
          videosByModule[module.id] = [];
          return [];
        }
      });

      await Promise.all(videoPromises);

      // Create a map of video progress from the API response
      const videoProgressArray = courseProgressResponse?.videoProgress ?? [];
      const videoProgressMap = new Map<string, { videoID: string; progress: number }>(
        videoProgressArray.map(vp => [vp.videoID, vp])
      );

      // Calculate progress for each module using API data
      const progress: Record<string, { watched: number; total: number; percent: number }> = {};

      modulesResponse.modules.forEach((module) => {
        const moduleVideos = videosByModule[module.id] || [];
        const totalVideos = moduleVideos.length;

        // Count watched videos based on API progress data
        // Backend returns progress as 0.0-1.0, videos are considered watched at >= 0.95 (95%)
        const watchedVideos = moduleVideos.filter((video: Video) => {
          const videoProgress = videoProgressMap.get(video.id);
          return videoProgress !== undefined && videoProgress.progress >= 0.95;
        }).length;

        progress[module.id] = {
          watched: watchedVideos,
          total: totalVideos,
          percent: totalVideos > 0 ? Math.round((watchedVideos / totalVideos) * 100) : 0
        };
      });

      setModuleProgress(progress);
    } catch (err) {
      console.error('Error loading course:', err);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  };

  // Don't redirect immediately, wait for the data to load
  if (!courseId) {
    return <Navigate to="/courses" replace />;
  }

  // If course is still loading, show loading state instead of redirecting
  if (!course) {
    return (
      <>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Lade Kurs...</p>
        </div>
      </>
    );
  }

  const filteredModules = modules.filter(module => {
    const matchesSearch = searchQuery === '' ||
      module.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (module.description && module.description.toLowerCase().includes(searchQuery.toLowerCase()));

    if (!matchesSearch) return false;

    if (showOnlyUnwatched) {
      const progress = moduleProgress[module.id];
      return progress && progress.percent < 100;
    }

    return true;
  });

  const totalVideos = Object.values(moduleProgress).reduce((sum, p) => sum + p.total, 0);
  const totalWatched = Object.values(moduleProgress).reduce((sum, p) => sum + p.watched, 0);
  const overallProgress = totalVideos > 0 ? Math.round((totalWatched / totalVideos) * 100) : 0;

  return (
    <>
      <div>
        <CourseBreadcrumb
          items={[
            { label: 'Kurse', href: '/courses' },
            { label: course.title }
          ]}
        />

        <div className="mb-8">
          <div className="mb-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <Link
                    to="/courses"
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
                  </Link>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {course.title}
                  </h1>
                </div>
                <div className="ml-9">
                  <MarkdownRenderer>
                    {course.description}
                  </MarkdownRenderer>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 ml-9 md:ml-0 md:flex-shrink-0">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {overallProgress}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Kursfortschritt
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {totalWatched} von {totalVideos} Videos
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Module durchsuchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
              />
            </div>

            <button
              onClick={() => setShowOnlyUnwatched(!showOnlyUnwatched)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 cursor-pointer ${
                showOnlyUnwatched
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className="w-4 h-4" />
              Nur nicht gesehene
            </button>
          </div>
        </div>

        {filteredModules.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || showOnlyUnwatched
                ? 'Keine Module gefunden. Versuche es mit anderen Suchkriterien.'
                : 'Noch keine Module verfügbar.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredModules.map((module) => {
              const progress = moduleProgress[module.id] ?? { watched: 0, total: 0, percent: 0 };
              // Free courses are only accessible without an active subscription; subscribers need an explicit unlock.
              const hasAccess = course && (hasCourseAccess(course.id as CourseID) || (course.free && !hasActiveSubscription()));

              return (
                <ModuleCard
                  key={module.id}
                  title={module.title}
                  subtitle={module.description}
                  thumbnail={module.thumbnail ? APIRoutes.getBlobContentUrl(module.thumbnail) : course.thumbnail ? APIRoutes.getBlobContentUrl(course.thumbnail) : ''}
                  progress={progress.percent}
                  href={hasAccess ? `/courses/${courseId}/modules/${module.id}` : `/feature-locked/course/${courseId}`}
                  lessonCount={progress.total}
                  watchedCount={progress.watched}
                />
              );
            })}
          </div>
        )}

        <PageBasedPagination
          currentPage={currentPage}
          totalCount={totalModules}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      </div>
    </>
  );
};

export default CourseDetail;
